/**
 * authToken.js — the ONE answer to "is the stored credential still usable?"
 * v242.169.0 · STALE-SESSION-1
 *
 * ★★★ WHY IT EXISTS. `utils/authStorage.js` owns WHERE auth lives. Nothing owned
 * WHETHER what lives there is still good, so nothing asked. The user store
 * persisted `isAuthenticated: true` and rehydrated it verbatim, which is how a
 * 90-day-expired token for a user id in NEITHER database rendered a normal
 * signed-in /home on 2026-07-27. v242.9.0 answered that with `validateSession`,
 * a round trip to /auth/me on boot — correct, and still one round trip late:
 * the signed-in screen is already painted when the answer arrives, and
 * `validateSession` deliberately KEEPS the session on any non-401 error, so an
 * expired token plus a flaky network still looks alive indefinitely.
 *
 * A JWT carries its own expiry. Reading it costs nothing and needs no network.
 *
 * ★★★ AND THE RULE THAT MATTERS MOST HERE: **"I CANNOT TELL" IS NOT "EXPIRED".**
 * Every function below returns a NEGATIVE (not expired) for a token it cannot
 * parse, a payload with no `exp`, or an `exp` that is not a finite number. Only
 * a present, finite, past `exp` returns true. The opposite convention — treat
 * unreadable as expired — would sign out every patient in the app on the day
 * the token format changed, and it would do it client-side where no server
 * error would ever be logged. The server remains the authority on validity;
 * this file only forecloses the case the client can prove by itself.
 *
 * ★ NO IMPORTS, DELIBERATELY. `scripts/check-session-expiry.mjs` imports this
 *   file directly in node to run the predicate against constructed tokens, so
 *   the gate tests an OUTCOME rather than the shape of a call site. A single
 *   app-level import here would end that.
 */

/** Decode a JWT payload. Returns null for anything that is not a readable JWT. */
export function decodeJwtPayload(token) {
  if (typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    while (b64.length % 4) b64 += '='
    const json = decodeURIComponent(
      atob(b64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    const payload = JSON.parse(json)
    return payload && typeof payload === 'object' ? payload : null
  } catch (_) {
    return null
  }
}

/**
 * Expiry as epoch MILLISECONDS, or null when the token does not state one.
 * `exp` is seconds in RFC 7519; a token that states it in milliseconds would
 * read as the year 56000 and simply never expire here, which is the safe way
 * round.
 */
export function tokenExpiresAt(token) {
  const payload = decodeJwtPayload(token)
  const exp = payload?.exp
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return null
  return exp * 1000
}

/**
 * True ONLY when the token states an expiry that has already passed.
 * `skewMs` expires a token slightly early so a request begun now does not
 * arrive after the server's own cutoff; it never makes a valid token invalid
 * by more than that window.
 */
export function isTokenExpired(token, skewMs = 30000) {
  const at = tokenExpiresAt(token)
  if (at == null) return false
  return at <= Date.now() + skewMs
}

/** Inverse convenience: a token we can positively still use. Absent = not usable. */
export function isTokenUsable(token) {
  if (!token) return false
  return !isTokenExpired(token)
}
